import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { UserMediaPermissionSnapshot } from '../../models/consent/media-permission.model';
import { MediaPermissionService } from './media-permission.service';

interface CacheEntry {
  value: UserMediaPermissionSnapshot;
  expiresAt: number;
}

@Injectable({ providedIn: 'root' })
export class RecipientPermissionCacheService {
  private readonly ttlMs = 60 * 1000;
  private readonly cache = new Map<string, CacheEntry>();

  constructor(
    private readonly mediaPermissionService: MediaPermissionService
  ) {}

  async getRecipientPermissions(
    userIds: string[]
  ): Promise<Record<string, UserMediaPermissionSnapshot>> {
    const now = Date.now();
    const result: Record<string, UserMediaPermissionSnapshot> = {};
    const ids = (userIds || []).filter(Boolean);

    // Always prefer backend as source of truth
    try {
      const resp = await firstValueFrom(
        this.mediaPermissionService.getPermissionsByUserIds(ids)
      );
      const rows = resp?.data || [];
      rows.forEach((row) => {
        if (!row?.userId) return;
        result[row.userId] = row;
        this.cache.set(row.userId, {
          value: row,
          expiresAt: now + this.ttlMs,
        });
      });
      return result;
    } catch {
      // fallback to cache if backend fails
      ids.forEach((id) => {
        const cached = this.cache.get(id);
        if (cached && cached.expiresAt > now) {
          result[id] = cached.value;
        }
      });
      return result;
    }
  }

  invalidate(userId: string): void {
    this.cache.delete(userId);
  }

  invalidateMany(userIds: string[]): void {
    (userIds || []).forEach((id) => this.cache.delete(id));
  }

  clear(): void {
    this.cache.clear();
  }
}
